// Tryb — design the event thumbnail in Canva.
//
// Three ways in: start a blank cover-shaped canvas, start from one of the
// team's brand templates (prefilled with the experience's name), or pick a design
// already in the facilitator's Canva account. Whichever they choose, Canva
// opens in a new tab and the finished artwork is pulled back here as the cover.

import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Icon, Input } from "../ds/index.js";
import { api } from "../lib/api.js";
import { useStudio } from "../lib/studioContext.js";

const railLabel = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

export default function CanvaPanel({ onDone }) {
  const { vals, setField, showToast, refresh } = useStudio();

  const [status, setStatus] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  // The design opened in Canva this session — kept so we can offer "I'm done"
  // without making them hunt for it again in the list.
  const [editing, setEditing] = useState(null);

  const title = vals["B1.01"] || "Your tryb";

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.canvaStatus();
      setStatus(s);
      return s;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, []);

  const loadLibrary = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const [d, t] = await Promise.all([api.canvaDesigns(q), api.canvaBrandTemplates(q)]);
      setDesigns(d.designs);
      setTemplates(t.unavailable ? null : t.templates);
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus().then((s) => {
      if (s?.connected) loadLibrary("");
      else setLoading(false);
    });
  }, [loadStatus, loadLibrary]);

  // The OAuth popup posts back here when consent is granted or refused.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || e.data?.source !== "tryb-canva") return;
      if (!e.data.ok) return showToast("Canva wasn’t connected", "Nothing was changed. You can try again.");
      loadStatus().then((s) => s?.connected && loadLibrary(""));
      showToast("Canva connected", "Your designs and brand templates are available here now.");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [loadStatus, loadLibrary, showToast]);

  const connect = () => {
    const w = 640;
    const h = 760;
    window.open(
      "/api/canva/authorize",
      "tryb-canva-auth",
      `width=${w},height=${h},left=${Math.max(0, (window.screen.width - w) / 2)},top=${Math.max(0, (window.screen.height - h) / 2)}`,
    );
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Canva? Covers you've already saved stay where they are.")) return;
    try {
      await api.canvaDisconnect();
      setDesigns([]);
      setTemplates(null);
      setEditing(null);
      await loadStatus();
      showToast("Canva disconnected", "Reconnect any time from here.");
    } catch (e) {
      showToast("Couldn’t disconnect", e.message);
    }
  };

  const openInCanva = (design) => {
    setEditing(design);
    if (design.editUrl) window.open(design.editUrl, "_blank", "noopener");
  };

  const createBlank = async () => {
    setBusyId("new");
    try {
      const { design } = await api.canvaCreateDesign(`${title} — cover`);
      openInCanva(design);
      showToast("Canva is open in a new tab", "Design your cover, then come back and press “Use this design”.");
    } catch (e) {
      showToast("Couldn’t start a Canva design", e.hint || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const useTemplate = async (template) => {
    setBusyId(template.id);
    try {
      // The server only fills fields the template actually declares.
      const { design } = await api.canvaUseBrandTemplate(template.id, `${title} — cover`, {
        title,
        name: title,
        heading: title,
        tagline: vals["B1.02"] || "",
        subtitle: vals["B1.02"] || "",
      });
      openInCanva(design);
      showToast("Template ready in Canva", "We filled in what we could. Tweak it, then come back here.");
    } catch (e) {
      showToast("Couldn’t use that template", e.hint || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const useAsCover = async (design) => {
    setBusyId(design.id);
    try {
      const { answer } = await api.canvaUseAsCover(design.id, design.title || title);
      setField("B1.07", answer);
      await refresh();
      showToast("Cover set from Canva", "We saved a copy, so it stays put even if the design changes later.");
      onDone?.();
    } catch (e) {
      showToast("Couldn’t bring that design in", e.hint || e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (!status) {
    return <Hint>{error ? error : "Checking Canva…"}</Hint>;
  }

  if (!status.configured) {
    return (
      <Hint>
        <strong style={{ display: "block", color: "var(--text-primary)", marginBottom: 6 }}>Canva isn’t set up on this server.</strong>
        {status.hint}
      </Hint>
    );
  }

  if (!status.connected) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 14, padding: "28px 4px" }}>
        <div
          style={{
            width: 72, height: 72, borderRadius: "var(--radius-lg)", background: "var(--navy-100)", color: "var(--navy-700)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="Sparkle" size={34} sw={1.8} />
        </div>
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-md)", fontWeight: 800 }}>Design it in Canva</div>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5, marginTop: 4, maxWidth: 520 }}>
            Connect your Canva account to use your own designs and brand templates as the cover. We only read your designs and export the
            one you pick — we never post anything to your account.
          </div>
        </div>
        <Button variant="primary" onClick={connect}>
          Connect Canva
        </Button>
        {error && <Hint>{error}</Hint>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 0, flex: 1, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Badge tone="success" dot>
          {status.account?.displayName ? `Canva · ${status.account.displayName}` : "Canva connected"}
        </Badge>
        <Button size="sm" variant="primary" onClick={createBlank} disabled={busyId === "new"}>
          {busyId === "new" ? "Opening Canva…" : "Start a blank cover"}
        </Button>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Input
            placeholder="Search your Canva designs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadLibrary(query)}
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => loadLibrary(query)}>
          Refresh
        </Button>
        <Button size="sm" variant="ghost" onClick={disconnect}>
          Disconnect
        </Button>
      </div>

      {editing && (
        <div
          style={{
            background: "var(--honey-100)",
            border: "1px solid var(--honey-300)",
            borderRadius: "var(--radius-lg)",
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>“{editing.title}” is open in Canva.</div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", marginTop: 2 }}>
              Finish it there, then bring it back — we’ll export the current version.
            </div>
          </div>
          {editing.editUrl && (
            <Button size="sm" variant="ghost" onClick={() => window.open(editing.editUrl, "_blank", "noopener")}>
              Reopen in Canva
            </Button>
          )}
          <Button size="sm" variant="primary" onClick={() => useAsCover(editing)} disabled={busyId === editing.id}>
            {busyId === editing.id ? "Bringing it in…" : "Use this design"}
          </Button>
        </div>
      )}

      {error && <Hint>{error}</Hint>}

      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, paddingRight: 4, minHeight: 0 }}>
        {templates?.length > 0 && (
          <section>
            <div style={{ ...railLabel, marginBottom: 10 }}>Brand templates</div>
            <Grid>
              {templates.map((t) => (
                <Tile
                  key={t.id}
                  title={t.title}
                  thumbnail={t.thumbnail}
                  busy={busyId === t.id}
                  cta="Use template"
                  busyLabel="Opening…"
                  onPick={() => useTemplate(t)}
                />
              ))}
            </Grid>
          </section>
        )}

        <section>
          <div style={{ ...railLabel, marginBottom: 10 }}>Your Canva designs</div>
          {loading ? (
            <Hint>Loading your designs…</Hint>
          ) : designs.length ? (
            <Grid>
              {designs.map((d) => (
                <Tile
                  key={d.id}
                  title={d.title}
                  thumbnail={d.thumbnail}
                  busy={busyId === d.id}
                  cta="Use as cover"
                  busyLabel="Exporting…"
                  onPick={() => useAsCover(d)}
                  onEdit={d.editUrl ? () => openInCanva(d) : null}
                />
              ))}
            </Grid>
          ) : (
            <Hint>
              Nothing in your Canva account yet — “Start a blank cover” opens a {status.coverSize?.width}×{status.coverSize?.height} canvas,
              the shape a tryb cover wants.
            </Hint>
          )}
        </section>
      </div>
    </div>
  );
}

function Grid({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))", gap: 12 }}>{children}</div>;
}

function Tile({ title, thumbnail, busy, cta, busyLabel, onPick, onEdit }) {
  return (
    <div
      style={{
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        background: "var(--surface-raised)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          aspectRatio: "3 / 2",
          background: "var(--sand-200)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {thumbnail ? (
          <img src={thumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Icon name="Sparkle" size={28} />
        )}
      </div>
      <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{ fontSize: "var(--text-sm)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={title}
        >
          {title}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="primary" onClick={onPick} disabled={busy} style={{ flex: 1 }}>
            {busy ? busyLabel : cta}
          </Button>
          {onEdit && (
            <Button size="sm" variant="ghost" onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Hint({ children }) {
  return (
    <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", lineHeight: 1.55, padding: "18px 4px", maxWidth: 560 }}>
      {children}
    </div>
  );
}
